import * as ko from 'knockout';
import 'knockout.validation';

import {
    IAddKidRequest,
    IPatientUpcomingConsultationDto,
    IStatus,
} from '../../Common/interfaces';
import Modal from '../../Common/Modal';
import { Texts } from '../../Common/Texts';
import { Utils } from '../../Common/Utils';
import { AlertViewModel } from '../AlertViewModel';
import { ReceiveVideoCall } from './ReceiveVideoCallFromDoctor';
import { ConsultationTypeEnum } from '../../Common/enums';

export class SelectPatientViewModel {
    public receiveVideoCall: ReceiveVideoCall;
    public profileState: KnockoutObservable<string>;
    public modalName: KnockoutObservable<string>;
    public modalCPR: KnockoutObservable<string>;
    public childModalError: KnockoutValidationGroup;
    public isSubmitting: KnockoutComputed<boolean>;
    public alertModal: AlertViewModel;
    public usersUpcomingBookingConsultationMap: IPatientUpcomingConsultationDto[];

    private profileStates = {
        ready: 'ready',
        saving: 'saving',
        saved: 'saved',
    };

    constructor(consultationType: ConsultationTypeEnum) {
        const self = this;
        self.usersUpcomingBookingConsultationMap = [];
        self.alertModal = new AlertViewModel();
        self.receiveVideoCall = new ReceiveVideoCall();

        self.profileState = ko.observable(self.profileStates.ready);

        self.modalName = ko.observable('').extend({
            required: {
                param: true,
                message: Texts.getResource('PleaseEnterFullName'),
            },
            fullname: {
                param: true,
                message: Texts.getResource('PleaseEnterFullName'),
            },
        });

        self.modalCPR = ko
            .observable('')
            .extend({ cprClean: true })
            .extend({
                required: {
                    param: true,
                    message: Texts.getResource('CprRequired'),
                },
                cpr: true,
                underCertainAge: 18,
            });

        self.childModalError = ko.validatedObservable([
            self.modalCPR,
            self.modalName,
        ]);
        self.childModalError.errors.showAllMessages(false);

        self.isSubmitting = ko.computed(() => {
            return self.profileState() == self.profileStates.saving;
        });

        Utils.ajaxCall(
            'GET',
            `api/patients/has-upcoming-video-consultation-per-user/${consultationType}`,
            (
                response: IPatientUpcomingConsultationDto[],
                textStatus,
                jqXhr,
            ) => {
                this.usersUpcomingBookingConsultationMap = response;
            },
        );
    }

    public validateIfPatientHaveUpcomingConsultationBeforeContinuing = (
        continuationUrl: string,
        patientUid: string,
    ) => {
        const self = this;
        const selectedUser = self.usersUpcomingBookingConsultationMap.find(
            (x) => x.UID == patientUid,
        );
        if (selectedUser && selectedUser.HasUpcomingVideoConsultation) {
            Modal.show('youAlreadyHaveUpcomingConsultation');
            return;
        }

        document.location.href = continuationUrl;
    };

    public closeYouAlreadyHaveUpcomingConsultation = () => {
        Modal.close('youAlreadyHaveUpcomingConsultation');
    };

    public addChildModal = () => {
        const self = this;
        self.modalName('');
        self.modalCPR(null);
        self.childModalError.errors.showAllMessages(false);
        self.profileState(self.profileStates.ready);
        Modal.show('addChildModal');
    };

    public addChild = () => {
        const self = this;

        if (self.profileState() !== self.profileStates.ready) {
            return;
        }

        self.profileState(self.profileStates.saving);

        if (!self.childModalError.isValid()) {
            self.profileState(self.profileStates.ready);
            self.childModalError.errors.showAllMessages(true);
            return;
        }

        const request: IAddKidRequest = {
            Name: self.modalName(),
            CPR: self.modalCPR(),
        };

        const callBackAjax = (
            response: IStatus<string>,
            textStatus: string,
            jqXhr: JQueryXHR,
        ): void => {
            if (response.HasErrors) {
                this.alertModal.openAlert(
                    response.Errors.map((e) => e.Value).join('\n'),
                );
                self.profileState(self.profileStates.ready);
                Modal.close('addChildModal');
                return;
            }
            Modal.close('addChildModal');
            self.profileState(self.profileStates.ready);
            window.location.reload();
        };

        const errorCallBack = (
            jqXhr: JQueryXHR,
            textStatus: string,
            error: string,
        ): void => {
            self.alertModal.openAlert(
                Texts.getResource('CouldNotCreateChild') + error,
            );
            self.profileState(self.profileStates.ready);
            Modal.close('addChildModal');
        };

        Utils.ajaxCall(
            'POST',
            'api/patients/child',
            callBackAjax,
            errorCallBack,
            JSON.stringify(request),
        );
    };
}
